import React, { useEffect, useState } from "react";
import { Box, Button, ButtonGroup, Container, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Link, Typography } from "@material-ui/core";
import Markdown from "react-markdown";
import { Apple } from "@material-ui/icons";
import { PasswordTextField, PlainTextField } from "components";
import { Auth } from "aws-amplify";
import styles from "./DashboardLoginView.module.scss";
import i18n from "../i18n";

// prop types
interface DashboardLoginViewProps {
    error: string | null,
    userError: string | null,
    isLoading: boolean,
    isCompayProfile: boolean,
    onForgotPassword: () => void,
    onLoginDashboard: (clubKey: string) => void,
    onLoginSocial: (provider: "apple" | "google") => void,
    onLoginUser: (email: string, password: string) => void,
}

// dashboard login screen
const DashboardLoginView = ({ error, userError, isLoading, isCompayProfile, onForgotPassword, onLoginDashboard, onLoginUser, onLoginSocial }: DashboardLoginViewProps) => {
    // ui state
    const [clubKey, setClubKey] = useState("");
    const [emailAddress, setEmailAddress] = useState("");
    const [password, setPassword] = useState("");
    const [showUserLogin, setShowUserLogin] = useState(true);
    const [showCompanyProfileRestrictedDialog, setShowCompanyProfileRestrictedDialog] = useState(false);
    const [showSignupRequiredDialog, setShowSignupRequiredDialog] = useState<boolean>(false);

    useEffect(() => {
        if (isCompayProfile && isLoading === false) {
            setShowCompanyProfileRestrictedDialog(true);
        } else {
            setShowCompanyProfileRestrictedDialog(false);
        }
    }, [isCompayProfile, isLoading]);

    useEffect(() => {
        if (userError === "userDoesNotExist") {
            setShowSignupRequiredDialog(true);
        } else {
            setShowSignupRequiredDialog(false);
        }
    }, [userError]);

    // browser not supported notice (happy IE)
    if (navigator.userAgent.match(/Trident/g) || navigator.userAgent.match(/MSIE/g)) {
        return (
            <Container maxWidth="xs" className={styles.container}>
                <img src="images/playsports_logo.svg" alt="PLAYSPORTS" className={styles.logo} />
                <Typography variant="h6" align="center">
                    {`${i18n.sadIE} `}
                    <Link href="https://www.google.com/chrome/" target="_blank">Chrome</Link>
                    {` ${i18n.or} `}
                    <Link href="https://www.mozilla.org/firefox" target="_blank">Firefox</Link>
                    .
                </Typography>
            </Container>
        );
    }

    // render
    const userLoginErrors = ["AuthError", "InvalidParameterException", "NotAuthorizedException", "UserNotFoundException"];
    return (
        <Container maxWidth="xs" className={styles.container}>
            {/* logo */}
            <img src="images/playsports_logo.svg" alt="PLAYSPORTS" className={styles.logo} />

            {/* user login */}
            {showUserLogin && (
                <>
                    <Typography variant="h6">
                        {i18n.userLogin}
                    </Typography>
                    <PlainTextField
                        autoFocus
                        disabled={isLoading}
                        error={userLoginErrors.includes(error!)}
                        label={i18n.emailAddress}
                        type="email"
                        value={emailAddress}
                        onChange={(e) => setEmailAddress(e.target.value)}
                    />
                    <PasswordTextField
                        disabled={isLoading}
                        error={userLoginErrors.includes(error!)}
                        helperText={userLoginErrors.includes(error!) && i18n.errors[error!]}
                        label={i18n.password}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onKeyPress={(e) => (e.key === "Enter") && onLoginUser(emailAddress, password)}
                    />
                    {/* {userLoginErrors.includes(error!) && ( */}
                    <Box style={{ marginTop: "8px" }}>
                        <Button onClick={onForgotPassword}>
                            {i18n.forgotPassword}
                        </Button>
                    </Box>
                    {/* )} */}

                    {/* login button and menu */}
                    <ButtonGroup
                        color="primary"
                        disabled={isLoading}
                        size="large"
                        variant="contained"
                    >
                        <Button onClick={() => onLoginUser(emailAddress, password)}>
                            {i18n.signIn}
                        </Button>
                    </ButtonGroup>
                    <Box className={styles.marginTop10}>
                        <Typography variant="subtitle2">
                            {i18n.orLable}
                        </Typography>
                    </Box>
                    <ButtonGroup color="primary" className={styles.socialButtonGroup}>
                        <Button
                            className={styles.appleButton}
                            size="large"
                            startIcon={<Apple className={styles.appleIcon} />}
                            onClick={() => onLoginSocial("apple")}
                        >
                            {i18n.signInWithApple}
                        </Button>
                        <Button
                            size="large"
                            className={styles.googleButton}
                            startIcon={<img src="images/googleColor.png" alt="PLAYSPORTS" className={styles.googleIcon} />}
                            onClick={() => onLoginSocial("google")}
                        >
                            {i18n.signInWithGoogle}
                        </Button>
                    </ButtonGroup>
                </>
            )}

            {/* dashboard login */}
            {!showUserLogin && (
                <>
                    <Typography variant="h6">
                        {i18n.dashboardLogin}
                    </Typography>
                    <PasswordTextField
                        autoFocus
                        disabled={isLoading}
                        error={error === "invalidClubKey"}
                        helperText={error === "invalidClubKey" && i18n.errors[error!]}
                        label={i18n.clubKey}
                        value={clubKey}
                        onChange={(e) => setClubKey(e.target.value)}
                        onKeyPress={(e) => (e.key === "Enter") && onLoginDashboard(clubKey)}
                    />
                    <Button
                        className={styles.loginButton}
                        color="primary"
                        disabled={isLoading}
                        size="large"
                        variant="contained"
                        onClick={() => onLoginDashboard(clubKey)}
                    >
                        {i18n.signIn}
                    </Button>
                </>
            )}

            {/* login as dashboard / user */}
            <Button className={styles.emailLoginButton} onClick={() => setShowUserLogin(!showUserLogin)}>
                {showUserLogin ? i18n.signInAsDashboard : i18n.signInAsUser}
            </Button>

            {/* copyright */}
            <Typography variant="body2" color="textSecondary" align="center">
                {`\u00A9 ${new Date().getFullYear()} PLAYSPORTS`}
            </Typography>

            {/* Company Profile Restriction Dialog */}
            <Dialog open={showCompanyProfileRestrictedDialog} onClose={() => setShowCompanyProfileRestrictedDialog(false)}>
                <DialogTitle>{i18n.permissionDeniedTitle}</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography>{i18n.permissionDeniedMessage}</Typography>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button color="primary" onClick={() => setShowCompanyProfileRestrictedDialog(false)}>
                        {i18n.ok}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Signup Required Dialog */}
            <Dialog open={showSignupRequiredDialog} onClose={() => setShowSignupRequiredDialog(false)}>
                <DialogTitle>{i18n.permissionDeniedTitle}</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography>{i18n.signupRequireddMessage}</Typography>
                            <Markdown linkTarget="_blank">{i18n.downloadMobileApp}</Markdown>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button
                        color="primary"
                        onClick={() => {
                            setShowSignupRequiredDialog(false);
                            Auth.deleteUser();
                        }}
                    >
                        {i18n.ok}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default DashboardLoginView;
